import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import type { Observable } from 'rxjs';
import { map, interval, switchMap, takeWhile, retry, timer, concat } from 'rxjs';
import { PatientInfoInterface } from 'src/app/components/edit-report/components/edit-section/edit-section.component';
import { ListInterface } from '../models/list-interface';
import type { ReportInterface } from '../models/report.interface';
import type { StudyDetailsInterface } from '../models/study-details.interface';
import type { GetStudiesReponse } from './studies.service';
import { GetStudyDetailsQuery, StudiesQuery, StudiesQueryVariables } from './studies.service';

export let GetReportOwnerHeaderQueryVariables: any = null;

export const  GetReportOwnerHeaderQuery = gql`query getReportOwnerHeader($userId: Long!, $clinicId: Long!, $studyId: Long!) {
  reportOwnerHeader(userId : $userId, clinicId : $clinicId, studyId : $studyId)
}
`;

export const  GetReportOwnerHeaderWithoutStudyQuery = gql`query getReportOwnerHeader($userId: Long!, $clinicId: Long!) {
  reportOwnerHeader(userId : $userId, clinicId : $clinicId)
}
`;

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  GetReportsByStudyQuery = gql`query getReportsByStudy($studyId: Long!, $skip: Int, $take: Int) {
    reports(where: { studyId: { eq: $studyId } }, skip: $skip, take: $take) {
      items {
        id
        studyId
        updatedAt
        name
        path
      }
      pageInfo {
        hasNextPage
      }
      totalCount
    }
  }`;

  GetReportByIdQuery = gql`query getReportsById($reportId: Long!) {
    reportById(id: $reportId) {
      id
      studyId
      htmlContent: htmlContentWithToken
      updatedAt
      name
      path
      patientName
      patientPid
      isLocked
      needsValidation
      pdfStatus
      htmlHash
      pdfErrorMessage
    }
  }`;

  constructor(private readonly apollo: Apollo) { }

  getReportById(reportId: number): Observable<ReportInterface> {
    return this.apollo.query<{ reportById: ReportInterface }>({
      query: this.GetReportByIdQuery,
      variables: { reportId }
    }).pipe(map(res => res.data.reportById));
  }

  getReportOwnerHeader(studyId: number, clinicId: number, userId: number ): Observable<string> {
    GetReportOwnerHeaderQueryVariables = { studyId, clinicId, userId };
    return this.apollo.query<{ reportOwnerHeader: string }>({
      query: GetReportOwnerHeaderQuery,
      variables: GetReportOwnerHeaderQueryVariables
    }).pipe(map(res => res.data.reportOwnerHeader));
  }

  getReportOwnerHeaderWithoutStudy(clinicId: number, userId: number ): Observable<string> {
    GetReportOwnerHeaderQueryVariables = { clinicId, userId };
    return this.apollo.query<{ reportOwnerHeader: string }>({
      query: GetReportOwnerHeaderWithoutStudyQuery,
      variables: GetReportOwnerHeaderQueryVariables
    }).pipe(map(res => res.data.reportOwnerHeader));
  }

  getReportsByStudy(studyId: number, skip = 0, take = 100): Observable<ReportInterface[]> {
    return this.apollo.query<{
      reports: {
        items: ReportInterface[];
        pageInfo: { hasNextPage: boolean };
        totalCount: number;
      };
    }>({
      query: this.GetReportsByStudyQuery,
      variables: { studyId, skip, take }
    }).pipe(map(res => res.data.reports?.items || []));
  }

  getSharedReports(skip: number, take: number, search?: string): Observable<ListInterface<ReportInterface>> {
    const searchVariables: any = { skip, take };

    const operatorArray: Record<string, any>[] = [];
    if (search) {
      operatorArray.push({
        or : [{
          study : {
            patient: {
              or: [
                { petName: { contains : search } },
                { ownerName: { contains : search } }
              ]
            }
          }
        },
        { name : { contains : search } },
        { ownerName : { contains : search } },
        { petName : { contains : search } }
        ]
      });
    }

    if (operatorArray.length) {
      searchVariables.where = { and: operatorArray };
    }

    return this.apollo.query<{ reports: ListInterface<ReportInterface> }>({
      query: gql`query getSharedReports($take: Int!, $skip: Int!, $where: ReportFilterInput) {
        reports(skip: $skip, take: $take, order: { updatedAt: DESC }, where: $where) {
          items {
            id
            studyId
            updatedAt
            name
            path
            patientName
            modalityTypeValues
            clinicId
            clinicName
            isLocked
            needsValidation

            study {
              id
              studyStatus
              previewPublicId
              nbInstances
              clinicId
            }
          }

          pageInfo {
            hasNextPage
          }
          totalCount
        }
      }`,
      variables: searchVariables,
      fetchPolicy: 'network-only'
    }).pipe(map(res => res.data.reports));
  }

  getReports(skip: number, take: number, clinicId: number, search?: string):  Observable<ListInterface<ReportInterface>> {

    const searchVariables: any = { skip, take, clinicId };

    const operatorArray: Record<string, any>[] = [];
    if (search) {
      operatorArray.push({
        or : [{
          study : {
            patient: {
              or: [
                {
                  petName: {
                    contains : search
                  }
                },
                {
                  ownerName: {
                    contains : search
                  }
                }
              ]
            }
          }
        },
        { name : { contains : search } },
        { ownerName : { contains : search } },
        { petName : { contains : search } }
        ]
      });
    }

    if (operatorArray.length) {
      searchVariables.where = {
        and: operatorArray
      };
    }

    return this.apollo.query<{ reportsPaged: ListInterface<ReportInterface> }>({
      query: gql`query getReports($take: Int!, $skip: Int!, $clinicId: Long!, $where: ReportFilterInput) {
        reportsPaged(skip: $skip, take: $take, clinicId : $clinicId, order: { updatedAt: DESC }, where: $where ) {
          items {
            id
            studyId
            updatedAt
            name
            path
            patientName
            modalityTypeValues
            clinicId
            clinicName
            isLocked
            needsValidation

            study {
              id
              studyStatus
              previewPublicId
              nbInstances
              clinicId
            }
          }

          pageInfo {
            hasNextPage
          }
          totalCount
        }
      }`,
      variables: searchVariables,
      fetchPolicy: 'network-only'
    }).pipe(map(res => res.data.reportsPaged));
  }

  updateReport(report: ReportInterface): Observable<ReportInterface | undefined> {
    return this.apollo.mutate<{ updateReport: ReportInterface }>({
      mutation : gql`mutation updateReport($dto: ReportUpdateDtoInput) {
        updateReport(dto: $dto) {
          id
          htmlContent: htmlContentWithToken
          studyId
          name
          path
          patientName
          pdfStatus
          pdfErrorMessage
        }
      }`,
      variables: {
        dto: {
          id: report.id,
          htmlContent: report.htmlContent
        }
      },
      refetchQueries: [{
        query: this.GetReportByIdQuery,
        variables: { reportId: report.id }
      },
      {
        query : GetStudyDetailsQuery,
        variables: { studyId: report.studyId }
      }]
    }).pipe(
      retry({ count: 4, delay: (_, retryCount) => timer([1000, 3000, 8000, 8000][retryCount - 1] ?? 8000) }),
      map(res => res.data?.updateReport)
    );
  }

  addReport(report: ReportInterface, patient: PatientInfoInterface | undefined): Observable<ReportInterface | undefined> {

    const refetchQueries = patient ? [{
      query : GetStudyDetailsQuery,
      variables: { studyId: report.studyId }
    }] : [];
    return this.apollo.mutate<{ addReport: ReportInterface }>({
      mutation : gql`mutation addReport($dto: ReportAddDtoInput!) {
        addReport(dto: $dto) {
          id
          htmlContent: htmlContentWithToken
          studyId
          name
          path
          patientName
          pdfStatus
          pdfErrorMessage
        }
      }`,
      variables: {
        dto: {
          htmlContent:report.htmlContent,
          id : report.id,
          studyId : report.studyId,
          name : report.name,
          ownerName : patient?.ownerName,
          petName : patient?.petName,
          patientPid: patient?.patientPid,
        }
      },
      update: (store, _data) => {
        if (report.study) {
        // Read the data from our cache for this query.
          const studiesQuery = store.readQuery<GetStudiesReponse>({ query: StudiesQuery, variables: StudiesQueryVariables });

          if (studiesQuery) {
            const listStudyItems = studiesQuery.studies.items.map(s=> ({ ...s }));

            const study = listStudyItems.find(s=>s.id === report.studyId);
            if (study) {
              study.nbReports++;
            }

            const newStudies = {
              studies: {
                items: listStudyItems,
                pageInfo: studiesQuery.studies.pageInfo,
                totalCount: studiesQuery.studies.totalCount
              }
            };
            store.writeQuery({ query: StudiesQuery, variables: StudiesQueryVariables, data: newStudies });
          }
        }
      },
      refetchQueries
    }).pipe(map(res => res.data?.addReport));
  }

  deleteReportFromStudy(reportId: number, studyId: number ): Observable<ReportInterface | undefined> {
    return this.apollo.mutate<{ deleteReportFromStudy: ReportInterface }>({
      mutation : gql`mutation deleteReportFromStudy($studyId: Long!, $reportId: Long!) {
        deleteReportFromStudy(studyId: $studyId, reportId: $reportId) {
          id
          htmlContent
          studyId
          name
          path
        }
      }`,
      variables: {
        reportId,
        studyId,
      },
      refetchQueries: [{
        query : GetStudyDetailsQuery,
        variables: { studyId }
      }],
      update: (store, { data }) => {
        const studyDetailsQuery = store.readQuery<GetStudyDetailsQueryResponseInterface>({
          query: GetStudyDetailsQuery,
          variables: { studyId }
        });

        if (studyDetailsQuery && data) {
          const study = { ...studyDetailsQuery.study };

          if (study) {
            study.reports = study.reports.filter(c=> c.id !== reportId);
            study.nbReports = study.reports.length;
          }

          store.writeQuery<GetStudyDetailsQueryResponseInterface>({ query: GetStudyDetailsQuery, variables: { studyId }, data : { study }  });
        }
      }
    }).pipe(map(res => res.data?.deleteReportFromStudy));
  }

  deleteReport(reportId: number): Observable<ReportInterface | undefined> {
    return this.apollo.mutate<{ deleteReport: ReportInterface }>({
      mutation : gql`mutation deleteReport($id: Long!) {
        deleteReport(id: $id) {
          id
          htmlContent
          studyId
          name
          path
        }
      }`,
      variables: {
        id : reportId,
      },
    }).pipe(map(res => res.data?.deleteReport));
  }

  shareReport(reportId: number, attachmentName: string ): Observable<string | undefined> {
    return this.apollo.mutate<{ shareReport: string }>({
      mutation : gql`mutation shareReport($reportId: Long!, $attachmentName: String!) {
        shareReport(reportId: $reportId, attachmentName: $attachmentName )
      }`,
      variables: {
        reportId,
        attachmentName,
      },
    }).pipe(map(res => res.data?.shareReport));
  }

  validateReport(reportId: number): Observable<ReportInterface | undefined> {
    return this.apollo.mutate<{ validateReport: ReportInterface }>({
      mutation : gql`mutation validateReport($reportId: Long!) {
        validateReport(id: $reportId) {
          id
          needsValidation
        }
      }`,
      variables: {
        id: reportId,
      },
    }).pipe(map(res => res.data?.validateReport));
  }

  renameReport(reportId: number, name: string): Observable<ReportInterface | undefined> {
    return this.apollo.mutate<{ renameReport: ReportInterface }>({
      mutation : gql`mutation renameReport($reportId: Long!, $name: String!) {
        renameReport(reportId: $reportId, name: $name) {
          id
          name
        }
      }`,
      variables: {
        reportId,
        name,
      },
    }).pipe(map(res => res.data?.renameReport));
  }

  regenerateReportPdf(reportId: number): Observable<ReportInterface | undefined> {
    return this.apollo.mutate<{ regenerateReportPdf: ReportInterface }>({
      mutation: gql`mutation regenerateReportPdf($reportId: Long!) {
        regenerateReportPdf(reportId: $reportId) {
          id
          pdfStatus
          pdfErrorMessage
        }
      }`,
      variables: { reportId },
    }).pipe(map(res => res.data?.regenerateReportPdf));
  }

  /** Polls reportById: first check at +1s, second at +3s, then every 5s.
   *  Early checks catch fast Gotenberg renders (2-5s) without waiting 5s
   *  for the first status update. Stops when pdfStatus is READY or ERROR,
   *  or after 1h max (720 checks × 5s + 3s early = ~1h). */
  pollReportUntilPdfReady(reportId: number): Observable<ReportInterface> {
    const query = () => this.apollo.query<{ reportById: ReportInterface }>({
      query: this.GetReportByIdQuery,
      variables: { reportId },
      fetchPolicy: 'network-only'
    }).pipe(map(res => res.data.reportById));

    const startedAt = Date.now();
    const ONE_HOUR_MS = 3600_000;

    return concat(
      timer(1000).pipe(switchMap(query)),
      timer(2000).pipe(switchMap(query)),
      interval(5000).pipe(switchMap(query))
    ).pipe(
      takeWhile(r => (r.pdfStatus === 'PENDING' || r.pdfStatus === 'IN_GENERATION')
                     && Date.now() - startedAt < ONE_HOUR_MS, true),
    );
  }
}

export interface GetStudyDetailsQueryResponseInterface {
  study: StudyDetailsInterface;
}
